import type { Kit, PractitionerNote } from '@repo/types';
import { IndicatorNamespace, type SeenIndicatorStore } from '@repo/utils';
import { seenIndicatorDB } from 'utils/seenIndicatorDB';

export type NewUpdateSurface = 'results' | 'actionPlan';

export interface ResultsSignatureInput {
  dateResultsReady: string | null | undefined;
  sciVersion: string | null | undefined;
  latestSciVersion: string | null | undefined;
}

export interface ActionPlanSignatureInput extends ResultsSignatureInput {
  publishedAt: string | null | undefined;
}

export type ResultsPortalIndicatorNamespace =
  IndicatorNamespace<NewUpdateSurface>;

const SIGNATURE_FIELD_SEPARATOR = '|';

const normalize = (value: string | null | undefined): string => value ?? '';

export const buildResultsSignature = (input: ResultsSignatureInput): string =>
  [
    normalize(input.dateResultsReady),
    normalize(input.sciVersion),
    normalize(input.latestSciVersion),
  ].join(SIGNATURE_FIELD_SEPARATOR);

export const buildActionPlanSignature = (
  input: ActionPlanSignatureInput,
): string =>
  [
    normalize(input.dateResultsReady),
    normalize(input.sciVersion),
    normalize(input.latestSciVersion),
    normalize(input.publishedAt),
  ].join(SIGNATURE_FIELD_SEPARATOR);

export const resultsSignatureFromKit = (
  kit: Pick<Kit, 'date_results_ready' | 'sci_version'>,
  latestSciVersion: string | null | undefined,
): string =>
  buildResultsSignature({
    dateResultsReady: kit.date_results_ready,
    sciVersion: kit.sci_version,
    latestSciVersion,
  });

export const actionPlanSignatureFromKit = (
  kit: Pick<Kit, 'date_results_ready' | 'sci_version'>,
  latestSciVersion: string | null | undefined,
  pracNote: Pick<PractitionerNote, 'is_published' | 'published_at'> | null,
): string =>
  buildActionPlanSignature({
    dateResultsReady: kit.date_results_ready,
    sciVersion: kit.sci_version,
    latestSciVersion,
    publishedAt:
      pracNote && pracNote.is_published ? pracNote.published_at : null,
  });

export const namespaceFor = (
  surface: NewUpdateSurface,
  tinyAccountId: string,
): ResultsPortalIndicatorNamespace => `newUpdate:${surface}:${tinyAccountId}`;

export const hasSeenUpdate = (
  surface: NewUpdateSurface,
  tinyAccountId: string,
  kitId: string,
  signature: string,
  store: SeenIndicatorStore = seenIndicatorDB,
): boolean =>
  store.hasSeenSignature(
    namespaceFor(surface, tinyAccountId),
    kitId,
    signature,
  );

export const markUpdateSeen = (
  surface: NewUpdateSurface,
  tinyAccountId: string,
  kitId: string,
  signature: string,
  store: SeenIndicatorStore = seenIndicatorDB,
): void => {
  store.markSignatureSeen(
    namespaceFor(surface, tinyAccountId),
    kitId,
    signature,
  );
};

export const parseNamespace = (namespace: string) => {
  const [, surface, tinyAccountId] = namespace.split(':');

  return { surface: surface as NewUpdateSurface, tinyAccountId };
};
